import orbAvatar from '@/assets/images/avatar/orb_avatar.png';
import electronMicroscopyImage from '@/assets/images/benchmarks/composite/electron-microscopy-d465ab9a.png';
import chemistryTaskImage from '@/assets/images/benchmarks/composite/task-6-reference-image.png';
import inriaLogo from '@/assets/images/brands/inria-logo.svg';
import milanoBicoccaLogo from '@/assets/images/brands/milano-bicocca-university-logo.svg';
import edinburghRoundelLogo from '@/assets/images/brands/university-of-edinburgh-roundel.svg';

/**
 * @typedef {Object} CompositeTask
 * @property {string} slug
 * @property {string} title
 * @property {'Physics' | 'Biology' | 'Chemistry' | 'Math'} domain
 * @property {string} difficulty
 * @property {string} requiredArtifact
 * @property {string} repoPath
 * @property {string} taskUrl
 */

const faviconUrl = (domain) => `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;

export const passAt1 = [
  { llm: 'claude-opus-4.6', score: 21.4 },
  { llm: 'gemini-3.1-pro', score: 18.6 },
  { llm: 'grok-4.20-beta', score: 5.7 },
  { llm: 'gpt-5.4', score: 4.3 },
];

export const domainBreakdown = [
  { domain: 'Physics', count: 20 },
  { domain: 'Biology', count: 20 },
  { domain: 'Chemistry', count: 23 },
  { domain: 'Math', count: 7 },
];

export const compositeLinks = {
  arxivAbstract: 'https://arxiv.org/abs/2604.09836',
  paperPdf: 'https://arxiv.org/pdf/2604.09836',
  datasetRepo: 'https://huggingface.co/datasets/portex/COMPOSITE-STEM',
  harborAdapter: 'https://github.com/portex-ai/portex-composit-harbor-adapter',
  taskIndex: 'https://github.com/portexai/TODO_COMPOSITE_STEM/tree/main/tasks',
};

export const compositeTaskSchemaFields = [
  'slug',
  'title',
  'domain',
  'difficulty',
  'requiredArtifact',
  'repoPath',
  'taskUrl',
];

export const compositeExplainers = {
  taskCuration:
    'Tasks are expert-curated and distributed across four STEM domains to emphasize realistic workflows instead of narrow academic QA. Each task is defined with a required end artifact so success can be checked against concrete outputs.',
  harborSubstrate:
    'The benchmark is Harbor-compatible, so models are evaluated inside an execution environment with tools and filesystem access. This makes the benchmark sensitive to planning, tool use, environment interaction, and execution reliability.',
};

export const compositeAbout = {
  title: 'About',
  paragraphs: [
    'AI agents hold growing promise for accelerating scientific discovery, but measuring that progress requires evaluations that are difficult and realistic. We built COMPOSITE-STEM with domain experts across the sciences to help locate the frontier of agent capabilities.',
  ],
};

export const compositeCitation = {
  bibtex: `@misc{waters2026compositestem,
  title = {COMPOSITE-STEM: Expert-Curated Agentic STEM Tasks for Evaluating Scientific Agents},
  author = {Waters, Kyle and Nuzzi, Lucas and Looram, Tadhg and COMPOSITE-STEM Contributors},
  year = {2026},
  eprint = {2604.09836},
  archivePrefix = {arXiv},
  primaryClass = {cs.LG},
  url = {https://arxiv.org/abs/2604.09836}
}`,
};

export const compositeOrganizers = [
  {
    name: 'Kyle Waters',
    affiliation: 'Portex AI',
    role: 'Organizing Team',
    logo: orbAvatar,
  },
  {
    name: 'Lucas Nuzzi',
    affiliation: 'Portex AI',
    role: 'Organizing Team',
    logo: orbAvatar,
  },
  {
    name: 'Tadhg Looram',
    affiliation: 'Portex AI',
    role: 'Organizing Team',
    logo: orbAvatar,
  },
];

export const compositeContributors = [
  {
    name: 'Alessandro Tomasiello',
    affiliation: 'University of Milano-Bicocca',
    role: 'Physics',
    logo: milanoBicoccaLogo,
  },
  {
    name: 'Ariel Ghislain Kemogne Kamdoum',
    affiliation: 'University of Calgary',
    role: 'Math',
    logo: faviconUrl('ucalgary.ca'),
  },
  {
    name: 'Bikun Li',
    affiliation: 'University of Chicago',
    role: 'Math',
    logo: faviconUrl('uchicago.edu'),
  },
  {
    name: 'Damien Sileo',
    affiliation: 'Inria',
    role: 'Math',
    logo: inriaLogo,
  },
  {
    name: 'Egor Kretov',
    affiliation: 'Fraunhofer Institute for Individualized Medical Technology IMTE',
    role: 'Biology',
    logo: faviconUrl('fraunhofer.de'),
  },
  {
    name: 'Francesco Fournier-Facio',
    affiliation: 'University of Cambridge',
    role: 'Math',
    logo: faviconUrl('cam.ac.uk'),
  },
  {
    name: 'Georgios Soloupis',
    affiliation: 'Independent',
    role: 'Biology',
    logo: null,
  },
  {
    name: 'Haile Kassahun',
    affiliation: 'McGill University',
    role: 'Biology',
    logo: faviconUrl('mcgill.ca'),
  },
  {
    name: 'Hew Wolff',
    affiliation: 'Independent',
    role: 'Math',
    logo: null,
  },
  {
    name: 'Jiaqi Cai',
    affiliation: 'Massachusetts Institute of Technology',
    role: 'Physics',
    logo: faviconUrl('mit.edu'),
  },
  {
    name: 'Lianghui Li',
    affiliation: 'École Polytechnique Fédérale de Lausanne',
    role: 'Chemistry',
    logo: faviconUrl('epfl.ch'),
  },
  {
    name: 'Marc Roth',
    affiliation: 'Queen Mary University of London',
    role: 'Math',
    logo: faviconUrl('qmul.ac.uk'),
  },
  {
    name: 'Mohinder Naiya',
    affiliation: 'Dot Ingredients',
    role: 'Chemistry',
    logo: faviconUrl('dotingredients.com'),
  },
  {
    name: 'Naixu Guo',
    affiliation: 'National University of Singapore',
    role: 'Physics',
    logo: faviconUrl('nus.edu.sg'),
  },
  {
    name: 'Qicheng Tang',
    affiliation: 'Georgia Institute of Technology',
    role: 'Physics',
    logo: faviconUrl('gatech.edu'),
  },
  {
    name: 'Richard Wheeler',
    affiliation: 'University of Edinburgh',
    role: 'Biology',
    logo: edinburghRoundelLogo,
  },
  {
    name: 'Samuele Sala',
    affiliation: 'Murdoch University',
    role: 'Chemistry',
    logo: faviconUrl('murdoch.edu.au'),
  },
  {
    name: 'Serguei Popov',
    affiliation: 'University of Porto',
    role: 'Math',
    logo: faviconUrl('up.pt'),
  },
  {
    name: 'Steven Dillman',
    affiliation: 'Stanford University',
    role: 'Physics',
    logo: faviconUrl('stanford.edu'),
  },
  {
    name: 'Yuqi Li',
    affiliation: 'Stony Brook University',
    role: 'Physics',
    logo: faviconUrl('stonybrook.edu'),
  },
];

export const compositeSummary = {
  title: 'COMPOSITE-STEM',
  subtitle:
    '70 expert-curated agentic tasks across Physics, Biology, Chemistry, and Math, compatible with the',
  harborLabel: 'Harbor Framework',
  harborUrl: 'https://harborframework.com/',
  tasks: 70,
  contributors: compositeContributors.length,
  domains: 4,
  substrate: 'Harbor',
};

const sharedSystemPackages = [
  'ffmpeg',
  'python3',
  'python3-venv',
  'ripgrep',
  'tesseract-ocr',
  'tesseract-ocr-eng',
];

const sharedPythonPackages = [
  'pandas',
  'numpy',
  'pillow',
  'pypdf',
  'pdfplumber',
  'openpyxl',
  'httpx',
  'python-docx',
  'python-pptx',
  'beautifulsoup4',
  'lxml',
  'mutagen',
  'python-dotenv',
  'pyyaml',
];

const sharedDockerfile = `FROM ubuntu:24.04

COPY --from=ghcr.io/astral-sh/uv:latest /uv /uvx /bin/

ENV DEBIAN_FRONTEND=noninteractive
ENV PIP_DISABLE_PIP_VERSION_CHECK=1
ENV PIP_NO_CACHE_DIR=1

RUN apt-get update \\
  && apt-get install -y --no-install-recommends \\
     ca-certificates \\
     ffmpeg \\
     python3 \\
     python3-venv \\
     ripgrep \\
     tesseract-ocr \\
     tesseract-ocr-eng \\
  && rm -rf /var/lib/apt/lists/*

WORKDIR /app

COPY requirements.txt /tmp/requirements.txt
RUN uv pip install --system -r /tmp/requirements.txt && rm /tmp/requirements.txt

COPY refs /app/refs`;

const createEnvironment = (copiedAssets = '/app/refs') => ({
  baseImage: 'ubuntu:24.04',
  workdir: '/app',
  copiedAssets,
  systemPackages: sharedSystemPackages,
  pythonPackages: sharedPythonPackages,
  dockerfile: sharedDockerfile,
});

export const compositeExampleTasks = [
  {
    slug: 'electron-microscopy',
    domain: 'Biology',
    title: 'Electron Microscopy',
    taskId: 'electron-microscopy_97c56090',
    taskText:
      'The agent must inspect the reference microscopy image, reason about the visible bend direction and axoneme geometry, choose one of five candidate doublet sets, and briefly justify the answer from the image evidence.',
    rawInstruction: `refs/d465ab9a.png is an image of a crop from a thin section electron microscopy image of the Leishmania parasite. The Leishmania parasite swims using a single flagellum which has an asymmetric paraxial structure with a lattice-like internal structure, the PFR, as shown in the thin section electron microscopy image on the on the left. An axoneme mutation leads to curling up of the flagellum, as shown in the scanning electron microscopy of a detergent-extracted cell on the right, with the axoneme and PFR visible. The preferred bending plane of the flagellum is determind by the central pair microtubules. Dynein motor proteins on the axoneme doublets cause microtubule sliding which leads to flagellum bending. Based on these images, the dyneins on which set of doublets are over-active, leading to the curl? A. Doublets 1, 2, 3, 8 and 9. B. Doublets 1, 2, 3 and 4. C. Doublets 3, 4, 5, 6, 7. D. Doublets 6, 7, 8, 9. E. Cannot be determined from the images. Briefly justify your choice using the visible bend direction and axoneme geometry, then on the last line provide only the letter corresponding to the answer from the list.

Reference file path: \`/app/refs/d465ab9a.png\`

After your reasoning, state your final answer clearly on a single line that starts with Answer: so it can be parsed automatically. Example: \`Answer: The capital of France is Paris.\`

Write your complete response (including reasoning and final answer) to \`/app/answer.txt\`.`,
    answerText:
      'The full response is written to a required artifact file so the benchmark can evaluate both reasoning quality and execution reliability in an environment-backed run.',
    imageSrc: electronMicroscopyImage,
    imageAlt: 'Electron microscopy reference image for the Leishmania flagellum task.',
    imageCaption:
      'Reference image from the task bundle showing thin-section and scanning electron microscopy views used in the prompt.',
    referencePath: '/app/refs/d465ab9a.png',
    submissionPath: '/app/answer.txt',
    environment: createEnvironment(),
    rubric: [
      {
        name: 'Final dynein-set choice',
        evaluator: 'LLM judge',
        weight: 30,
        criterion:
          'The submission ultimately selects option D, meaning the over-active dyneins are on doublets 6, 7, 8, and 9.',
      },
      {
        name: 'Bend direction from PFR placement',
        evaluator: 'LLM judge',
        weight: 25,
        criterion:
          'The submission explains that the paraflagellar rod lies adjacent to doublets 4 to 6 and appears on the outside of the curve, so the flagellum is bending away from the PFR and towards doublet 1.',
      },
      {
        name: 'Candidate doublet sets narrowed correctly',
        evaluator: 'LLM judge',
        weight: 20,
        criterion:
          'The submission narrows the dynein candidates to the two bend-compatible groups {1,2,3,4} and {6,7,8,9}, or an equivalent pair of alternatives, based on axoneme geometry.',
      },
      {
        name: 'Minus-end-directed dynein reasoning',
        evaluator: 'LLM judge',
        weight: 25,
        criterion:
          'The submission uses microtubule-sliding or minus-end-directed dynein reasoning to conclude that doublets 6, 7, 8, and 9 are the over-active set.',
      },
    ],
    sampleRun: {
      note:
        'This sample run is hardcoded from a real Harbor execution using the bundled task files and verifier outputs.',
      agent: {
        name: 'portex-multimodal',
        version: '0.2.0',
        model: 'openrouter/openai/gpt-5.4',
        parser: 'json',
        temperature: '0.7',
        sessionId: 'edd6ab16-18ac-4dd5-838f-f004040e091f',
      },
      outcome: {
        status: 'Failed',
        statusTone: 'failed',
        scorePercent: 20,
        reward: '0.20',
        grade: 'I',
        finalAnswer: 'B',
        expectedAnswer: 'D',
        summary:
          'The run produced the required artifact and a parseable final answer, but the verifier marked the submission incorrect overall.',
      },
      timeline: [
        {
          step: '01',
          title: 'Task injected into a Harbor session',
          source: 'Harness prompt',
          detail:
            'The run starts with the full instruction, attached microscopy reference image, and the required /app/answer.txt artifact contract.',
        },
        {
          step: '02',
          title: 'Agent writes and verifies answer.txt',
          source: 'portex-multimodal',
          detail:
            'The agent reasons from the PFR orientation, selects option B, writes /app/answer.txt, and cats the file to confirm the artifact contents.',
        },
        {
          step: '03',
          title: 'Verifier grades the finished run',
          source: 'criteria.json + verifier',
          detail:
            'The verifier scores the run at 20/100. One rubric item passes by majority vote, but the final dynein-set choice and two other criteria fail.',
        },
      ],
      submissionPreview: `In the thin-section image, the paraflagellar rod (PFR) is attached asymmetrically to one side of the axoneme, letting us identify axoneme orientation. In the curled detergent-extracted flagellum, the PFR is visible on the outside (convex side) of the bend. Flagella bend toward the side whose doublets are relatively shortened by dynein-driven sliding, so the active dyneins must be on the side opposite the PFR. In the standard Leishmania/trypanosomatid axoneme geometry, the doublets opposite the PFR are doublets 1, 2, 3 and 4. Therefore those dyneins are over-active.

B
Answer: B`,
      criteriaResults: [
        {
          name: 'Final dynein-set choice',
          weight: 30,
          passed: false,
          grade: 'I',
          explanation: 'Majority vote: 1/10 judges passed',
        },
        {
          name: 'Bend direction from PFR placement',
          weight: 25,
          passed: false,
          grade: 'I',
          explanation: 'Majority vote: 5/10 judges passed',
        },
        {
          name: 'Candidate doublet sets narrowed correctly',
          weight: 20,
          passed: true,
          grade: 'C',
          explanation: 'Majority vote: 6/10 judges passed',
        },
        {
          name: 'Minus-end-directed dynein reasoning',
          weight: 25,
          passed: false,
          grade: 'I',
          explanation: 'Majority vote: 1/10 judges passed',
        },
      ],
    },
  },
  {
    slug: 'task-6-chemistry',
    domain: 'Chemistry',
    title: 'Cycloaddition Product Naming',
    taskId: 'task_6',
    taskText:
      'The agent must inspect the reaction scheme, infer the product of an azacyclic 1,2,3-triene precursor reacting with 1,1-diethoxyethene, and return the final product as an exact IUPAC name.',
    rawInstruction: `1,2,3-cyclohexatriene and substituted derivatives can engage in several types of cycloaddition and nucleophilic addition reactions, providing an unconventional means to access structurally complex molecules. Researchers have made an azacyclic 1,2,3-triene precursor, 1-methyl-6-oxo-4-(triethylsilyl)-1,6-dihydropyridin-3-yl 1,1,2,2,3,3,4,4,4-nonafluorobutane-1-sulfonate, which was reacted with 1,1-diethoxyethene in the presence of 18-crown-6 (2.0 equiv) and KF (2.0 equiv) in an inert environment at 60 °C for 50 hours to form the final product. What is the IUPAC name of the final product?. Reference image: refs/task_6_reference_image.png

Reference file path: \`/app/refs/task_6_reference_image.png\`

After your reasoning, state your final answer clearly on a single line that starts with Answer: so it can be parsed automatically. Example: \`Answer: The capital of France is Paris.\`

Write your complete response (including reasoning and final answer) to \`/app/answer.txt\`.`,
    answerText:
      'This chemistry task is checked with a single exact-match criterion against the final product name written to the required artifact.',
    imageSrc: chemistryTaskImage,
    imageAlt: 'Reaction scheme reference image for the chemistry naming task.',
    imageCaption:
      'Reference reaction scheme from the task bundle showing the azacyclic triene precursor, reagent, and product scaffold used in the prompt.',
    referencePath: '/app/refs/task_6_reference_image.png',
    submissionPath: '/app/answer.txt',
    environment: createEnvironment(),
    rubric: [
      {
        name: 'Answer correctness',
        evaluator: 'Exact match',
        weight: 100,
        criterion: '8,8-diethoxy-3-methyl-3-azabicyclo[4.2.0]octa-1,5-dien-4-one',
      },
    ],
    sampleRun: null,
  },
  {
    slug: 'cairo-pentagonal-tiling-sequence',
    domain: 'Math',
    title: 'Cairo Pentagonal Tiling Growth',
    taskId: 's0NJgi',
    taskText:
      'The agent must reason about neighborhood growth in a Cairo pentagonal tiling, iteratively expand a two-tile seed around a degree-4 vertex, and compute the next five values of the resulting tile-count sequence.',
    rawInstruction: `Take $S$ to be the set of closed tiles in a Cairo pentagonal tiling.
Given a subset $T \\subseteq S$, define $A(T)$ to be the set of tiles which meet some tile in $T$ (in at least one vertex).
Take a vertex $v$ where 4 tiles meet.
Take $T_0$ to be a set of two tiles which contain $v$ and share an edge, and $T_{i + 1} = A(T_i)$ for all $i$.
Define $n_i$ as the size of $T_i$, giving a sequence $(n_i)_i$ with $n_0 = 2$ and $n_1 = 11$.
What are the next 5 values of $n_i$?
Give your final answer in the form: "(a,b,c,d,e)".

Reference file path: \`(none)\`

After your reasoning, state your final answer clearly on a single line that starts with Answer: so it can be parsed automatically. Example: \`Answer: The capital of France is Paris.\`

Write your complete response (including reasoning and final answer) to \`/app/answer.txt\`.`,
    answerText:
      'This math task is graded semantically across the five requested sequence values, so the verifier checks each term of the Cairo-tiling growth sequence separately.',
    imageSrc: null,
    imageAlt: '',
    imageCaption: '',
    referencePath: '(none)',
    submissionPath: '/app/answer.txt',
    environment: createEnvironment('/app/refs (placeholder only)'),
    rubric: [
      {
        name: 'Value of n_2',
        evaluator: 'LLM judge',
        weight: 20,
        criterion: 'The submission identifies n_2 as 28.',
      },
      {
        name: 'Value of n_3',
        evaluator: 'LLM judge',
        weight: 20,
        criterion: 'The submission identifies n_3 as 54.',
      },
      {
        name: 'Value of n_4',
        evaluator: 'LLM judge',
        weight: 20,
        criterion: 'The submission identifies n_4 as 88.',
      },
      {
        name: 'Value of n_5',
        evaluator: 'LLM judge',
        weight: 20,
        criterion: 'The submission identifies n_5 as 130.',
      },
      {
        name: 'Value of n_6',
        evaluator: 'LLM judge',
        weight: 20,
        criterion: 'The submission identifies n_6 as 180.',
      },
    ],
    sampleRun: null,
  },
  {
    slug: 'qfi-squeezed-coherent-state',
    domain: 'Physics',
    title: 'QFI of a Squeezed Coherent State',
    taskId: 'prob_26',
    taskText:
      'The agent must derive the small-theta quantum Fisher information for a squeezed coherent state under a Gaussian dephasing channel, while explicitly avoiding symbolic-package shortcuts.',
    rawInstruction: `Consider a completely-positive trace-preserving map $\\mathcal{E}_\\theta$ for a bosonic mode, which encodes the parameter $\\theta$ in the following way:
$$
\\rho(\\theta) = \\mathcal{E}_\\theta(\\rho(0)) = \\frac{1}{\\sqrt{2\\pi \\theta}}\\int_{-\\infty}^{\\infty} e^{-\\frac{x^2}{2\\theta}} e^{-ix\\hat{n}}\\rho(0) e^{ix\\hat{n}}\\;\\mathrm{d}x\\;,
$$
where $\\hat{n}$ is the particle number operator, and $\\rho(0)$ is the input state. Now we consider $\\rho(0) = \\ket{\\alpha, r}\\bra{\\alpha, r}$ as the density matrix of the squeezed coherent state, which is defined as follows
$$
\\ket{{\\alpha, r}} = e^{\\alpha a^\\dagger - \\alpha^* a} e^{\\frac{r}{2}(a^2 - a^{\\dagger 2})}\\ket{0}\\;,
$$
where $\\ket{0}$ is the vacuum state, $a$ is the annihilation operator.
Please find the quantum Fisher information (based on symmetric logarithmic derivative) at $\\theta\\to 0$ in terms of $\\alpha\\in\\mathbb{C}$ and $r\\in\\mathbb{R}$.
Solving this problem with SymPy or QuTiP package in Python is prohibited and will be marked as wrong.
Show your reasoning.

Reference file path: \`(none)\`

After your reasoning, state your final answer clearly on a single line that starts with Answer: so it can be parsed automatically. Example: \`Answer: The capital of France is Paris.\`

Write your complete response (including reasoning and final answer) to \`/app/answer.txt\`.`,
    answerText:
      'This physics task is graded semantically: the verifier checks the Gaussian-channel derivative, the pure-state QFI reduction, the squeezed-state moments, and the final closed-form expression.',
    imageSrc: null,
    imageAlt: '',
    imageCaption: '',
    referencePath: '(none)',
    submissionPath: '/app/answer.txt',
    environment: createEnvironment('/app/refs (placeholder only)'),
    rubric: [
      {
        name: 'Gaussian channel differentiation',
        evaluator: 'LLM judge',
        weight: 20,
        criterion:
          'The submission differentiates the Gaussian integral representation and derives the dephasing master equation d rho / d theta = n rho n - 1/2 {n^2, rho}, or an equivalent result.',
      },
      {
        name: 'QFI reduction at theta -> 0',
        evaluator: 'LLM judge',
        weight: 25,
        criterion:
          'The submission reduces the symmetric-logarithmic-derivative quantum Fisher information at theta -> 0 to an expression built from <n> and <n^2> for the initial pure state, or an equivalent variance-based expression.',
      },
      {
        name: 'Moments of the squeezed coherent state',
        evaluator: 'LLM judge',
        weight: 25,
        criterion:
          'The submission correctly computes the needed moments of the squeezed coherent state, including <n> = |alpha|^2 + sinh^2 r and the corresponding <n^2> expression.',
      },
      {
        name: 'Final QFI formula',
        evaluator: 'LLM judge',
        weight: 30,
        criterion:
          'The submission ultimately concludes that the quantum Fisher information is (1/16) |4 cosh(2r) |alpha|^2 + cosh(4r) - 1|^2, or an equivalent algebraic form.',
      },
    ],
    sampleRun: null,
  },
];

export const compositeContact = {
  email: 'hello@portexai.com',
};

export const compositeLimitations = [
  'Report runs used max_turns=10.',
  'If no required artifact is produced, such as /app/answer.txt, the run counts as a failure.',
];

/** @type {CompositeTask[]} */
export const compositeTasks = [];
